import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api, { refreshSessionToken } from '../lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('auth_token');
    const savedUser = localStorage.getItem('auth_user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      }
    }
    setLoading(false);
  }, []);

  const fetchUnreadCount = useCallback(async () => {
    try {
      const res = await api.get('/notifications/unread-count');
      setUnreadCount(res.data.count);
    } catch {
      // ignore
    }
  }, []);

  useEffect(() => {
    if (user) {
      fetchUnreadCount();
      const interval = setInterval(fetchUnreadCount, 60000);
      return () => clearInterval(interval);
    }
  }, [user, fetchUnreadCount]);

  // Tiene viva la sessione finche' l'utente lavora (e quando torna sulla scheda dopo una pausa).
  useEffect(() => {
    if (!user) return undefined;
    let last = Date.now();
    const renew = () => { last = Date.now(); refreshSessionToken().catch(() => {}); };
    const interval = setInterval(renew, 10 * 60 * 1000);
    renew(); // al caricamento rinnova subito: imposta anche il cookie per aprire i file
    const onVisible = () => { if (document.visibilityState === 'visible' && Date.now() - last > 5 * 60 * 1000) renew(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', onVisible);
    };
  }, [user]);

  const login = async (email, password) => {
    const res = await api.post('/auth/login', { email, password });
    const { token, user: userData } = res.data;
    localStorage.setItem('auth_token', token);
    localStorage.setItem('auth_user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    api.post('/auth/logout').catch(() => {});
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    setUser(null);
    setUnreadCount(0);
  };

  const hasPermission = (module, action) => {
    if (!user) return false;
    if (user.role_name === 'admin') return true;
    const perms = user.permissions || [];
    return perms.includes(`${module}:${action}`) || perms.includes(`${module}:all`);
  };

  const isRole = (...roles) => {
    return user && roles.includes(user.role_name);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPermission, isRole, unreadCount, fetchUnreadCount }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
